import { Link } from "react-router-dom";
import styled from "styled-components";
import SimpleLogo from "./logo/TextLogo";
import Spacer from "./Spacer";
import React from "react";
import ThemeToggle from "./ThemeToggle";
import { siteUrl } from "../site/resolveSite";
import { ABOUT_ROUTE } from "../routes";


const Container = styled.div`
  display: flex;
  position: sticky;
  justify-content: flex-end;
  top: 0;
  align-items: center;
  column-gap: 32px;
  background-color: ${({ theme }) => theme.colour.DARK_GREY};
  box-sizing: border-box;
  z-index: 5;
  height: ${({theme}) => theme.dimensions.NAVBAR_HEIGHT}px;
  padding: 0 ${({theme}) => theme.dimensions.NAVBAR_PADDING}px;

  /* Below the tablet breakpoint the product lock-ups and the full-height bar
     stop fitting, so the bar wraps to its own rows and sheds fixed height. */
  @media (max-width: 60rem) {
    flex-wrap: wrap;
    justify-content: center;
    height: auto;
    min-height: 0;
    column-gap: 20px;
    row-gap: 8px;
    padding: 12px 16px;
  }
`

/* Both only make sense while the bar is a single row: wrapped, the spacer strands
   the links mid-row and the divider separates nothing. */
const NavSpacer = styled(Spacer)`
    @media (max-width: 60rem) {
        display: none;
    }
`

const NavDivider = styled.div`
    width: 2px;
    height: 1.4em;
    background-color: ${({ theme }) => theme.colour.COLOUR_MID_DARK};
    border-radius: 1px;
    flex-shrink: 0;

    @media (max-width: 60rem) {
        display: none;
    }
`

const MenuItem = styled(Link)`
  flex-basis:auto;
  display: flex;
  justify-content:center;
  align-items: center;
  color: ${({ theme }) => theme.colour.OFF_WHITE};
  font-family: ${({ theme }) => theme.font.FONT_SANS_RED_HAT_DISPLAY};
  font-size: ${({ theme }) => theme.dimensions.FONT_SIZE_SMALL}px;
  font-weight: 400;
  white-space: nowrap;
  background: none;
  transition: opacity .125s ease-in;

  /* Wrapped bar: the links are the only tap targets, so give them a finger's height. */
  @media (max-width: 60rem) {
    padding: 6px 0;
  }

  &:hover {
    opacity: 0.8;
    color: ${({theme}) => theme.colour.OFF_WHITE};
  }
`

const ExternalMenuItem = MenuItem.withComponent('a');

const ProductGroup = styled.div`
  display: flex;
  align-items: center;
  column-gap: 24px;
`

const ProductLink = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  row-gap: 0.16em;
  color: ${({ theme }) => theme.colour.OFF_WHITE};
  opacity: 0.85;
  font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
  /* 0.9 optically matches the sans nav links at the same nominal size */
  font-size: ${({ theme }) => theme.dimensions.FONT_SIZE_SMALL * 0.9}px;
  white-space: nowrap;
  transition: opacity .08s ease-in;

  @media (max-width: 60rem) {
    padding: 6px 0;
  }

  &:hover {
    opacity: 1;
    color: ${({ theme }) => theme.colour.OFF_WHITE};
  }
`

const ProductName = styled.span`
  font-family: ${({ theme }) => theme.font.FONT_SANS_RED_HAT_DISPLAY};
  /* Red Hat Display runs smaller than JetBrains Mono at the same nominal size */
  font-size: 1.05em;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`

const ProductDescriptor = styled.span`
  font-size: 0.6em;
  font-weight: 500;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colour.LIGHT_GREY};

  /* The strapline is the first thing to go when the bar has to wrap. */
  @media (max-width: 60rem) {
    display: none;
  }
`

const ExternalProductLink = ProductLink.withComponent('a');

type NavigationBarProps = {
  isLightMode: boolean;
  onToggleTheme: () => void;
};

export default function NavigationBar({ isLightMode, onToggleTheme }: NavigationBarProps){

    return (
        <Container>
            <SimpleLogo/>
            <NavSpacer/>
            <MenuItem to='/#services'>
                Services
            </MenuItem>
            <ExternalMenuItem href={siteUrl('education')}>
                Courses
            </ExternalMenuItem>
            <MenuItem to={ABOUT_ROUTE}>
                About
            </MenuItem>
            <MenuItem to='/#contact'>
                Contact
            </MenuItem>
            <NavDivider />
            <ProductGroup>
                <ExternalProductLink href={siteUrl('engine')}>
                    <ProductName>Discreet Engine</ProductName>
                    <ProductDescriptor>Voxel graphics</ProductDescriptor>
                </ExternalProductLink>
                <ExternalProductLink href={siteUrl('hardware')}>
                    <ProductName>OMH</ProductName>
                    <ProductDescriptor>Build your cloud</ProductDescriptor>
                </ExternalProductLink>
            </ProductGroup>
            <ThemeToggle isLightMode={isLightMode} onToggle={onToggleTheme} />
        </Container>
    )
}
